import React from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  useTheme,
} from '@mui/material';
import { useRouter } from '@tanstack/react-router';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SpeedIcon from '@mui/icons-material/Speed';
import CampaignIcon from '@mui/icons-material/Campaign';
import aboutUsImage from '../assets/Landing_Page2.png';

const FeatureCard = ({ icon, title, description }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        backgroundColor: 'transparent',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2, color: theme.palette.primary.main }}>{icon}</Box>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
        <Button sx={{ mt: 2 }} variant="text" color="primary" endIcon={<span>→</span>}>
          Learn More
        </Button>
      </CardContent>
    </Card>
  );
};

const About = () => {
  const router = useRouter();
  const theme = useTheme();

  const features = [
    {
      icon: <EditNoteIcon sx={{ fontSize: 40 }} />,
      title: 'Self-driven algorithm generation and continuous learning.',
      description:
        'Arcogni autonomously analyzes data, identifies patterns, and evolves its own models—reducing manual effort and accelerating scientific and engineering breakthroughs.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'High-precision insights for complex technical challenges.',
      description:
        'From multidimensional datasets to real-time sensor data, Arcogni delivers accurate analysis, performance evaluations, and actionable intelligence to guide critical decisions.',
    },
    {
      icon: <CampaignIcon sx={{ fontSize: 40 }} />,
      title: 'Transform discoveries into scalable solutions faster.',
      description:
        "Arcogni integrates seamlessly into research and engineering workflows, enabling rapid experimentation, optimization, and deployment across diverse domains.",
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.paper',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                Scale scientific research to new heights.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Arcogni leverages artificial intelligence to enhance data analysis, enabling the efficient aggregation of small evolutionary discoveries into generational breakthroughs that transform innovation across disciplines. Our capabilities include Artificial Intelligence as a Service (AIAAS), providing software that uses AI to generate algorithms for research and development in data analysis, as well as Platform as a Service (PAAS) solutions featuring computer software platforms specifically built to support algorithm generation for research and development in data analysis.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => router.navigate({ to: '/chat' })}
                  sx={{
                    bgcolor: '#BF2924',
                    '&:hover': {
                      bgcolor: '#a61f1b',
                    },
                  }}
                >
                  Get Started for Free
                </Button>
                <Button variant="outlined" size="large">
                  See How It Works
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={aboutUsImage}
                alt="About Us Illustration"
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: 3,
                  overflow: 'hidden',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="overline" color="primary">
            Key Features
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
            }}
          >
            Everything You Need for Smarter, Faster,
            <br />
            and More Informed Innovation
            and More Informed Innovation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            Arcogni combines advanced autonomous algorithms with an intuitive, user-friendly interface to help teams analyze complex data, generate meaningful insights, and refine strategies with confidence. Whether you are driving scientific discovery, advancing engineering solutions, or shaping data guided decisions, Arcogni provides the intelligence and agility needed to accelerate progress across missions and disciplines.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeatureCard {...feature} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: '#001233',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 600,
            }}
          >
            Join us on this transformative journey
            <br />
            with Arcogni as we scale scientific
            <br />
            research to new heights.
          </Typography>
          <Typography sx={{ mb: 4, opacity: 0.8 }}>
            As our platform grows, we empower users to unlock the full potential of their knowledge,
            driving unprecedented advancements in their fields.
          </Typography>
          <Box
            component="form"
            sx={{
              display: 'flex',
              gap: 1,
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            <TextField
              fullWidth
              placeholder="Input work email"
              variant="outlined"
              sx={{
                bgcolor: 'white',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#BF2924',
                '&:hover': {
                  bgcolor: '#a61f1b',
                },
              }}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
